import { Group } from "@fairfleet/geotab";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ASSET_TYPE_GROUPS } from "../utils/assetTypes";
import { GroupsTruncatedAlert } from "@/components/GroupsTruncatedAlert";
import { useGroupPickerHint } from "@/hooks/groups/useGroupPickerHint";
import { groupFilterOptions } from "@/utilities/groupFilterOptions";

type Props = {
  assetTypeGroups: Group[];
  allGroups: Group[];
  assetTypeGroup: Group | null;
  groups: Group[];
  onAssetTypeGroupChange: (group: Group | null) => void;
  onGroupsChange: (groups: Group[]) => void;
};

export default function AssetGroupsCard({
  assetTypeGroups,
  allGroups,
  assetTypeGroup,
  groups,
  onAssetTypeGroupChange,
  onGroupsChange,
}: Props) {
  const { t } = useTranslation();
  const groupOptions = useMemo(
    () => allGroups.filter(group => group.id !== assetTypeGroup?.id),
    [allGroups, assetTypeGroup?.id],
  );
  const groupPickerHint = useGroupPickerHint(groupOptions.length);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("assets.assetTypeGroupTitle")}
      </Typography>
      <Autocomplete
        options={assetTypeGroups}
        value={assetTypeGroup}
        onChange={(_, value) => onAssetTypeGroupChange(value)}
        getOptionLabel={option =>
          t(
            ASSET_TYPE_GROUPS.find(groupType => groupType.backendName === option.name)?.labelKey ??
              option.name ??
              option.id ??
              "",
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={assetTypeGroups.length === 0}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            label={t("assets.assetTypeGroupLabel")}
            placeholder={t("assets.assetTypeGroupPlaceholder")}
          />
        )}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1 }}>
        {t("assets.assetTypeGroupHint")}
      </Typography>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("assets.groupsTitle")}
      </Typography>
      <GroupsTruncatedAlert sx={{ mb: 1 }} />
      <Autocomplete
        multiple
        options={groupOptions}
        value={groups}
        onChange={(_, value) => onGroupsChange(value)}
        getOptionLabel={option => option.name ?? option.id ?? ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterSelectedOptions
        filterOptions={groupFilterOptions}
        disableCloseOnSelect
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            label={t("assets.groupSearchLabel")}
            placeholder={t("assets.groupSearchPlaceholder")}
            helperText={groupPickerHint}
          />
        )}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1 }}>
        {t("assets.groupSectionHint")}
      </Typography>

      {groups.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("assets.noGroups")}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {groups.map(group => (
            <Chip key={group.id} label={group.name ?? group.id ?? "-"} size="small" />
          ))}
        </Stack>
      )}
    </Paper>
  );
}
